import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BookingProductType } from '@products/models/booking.model';
import { PreferredBookingService } from '@products/services/preferred-booking.service';
import { ProductBookingService } from '@products/services/product-booking.service';
import { BOOKING_DEBOUNCE_TIME } from '@shared/tokens';
import { PackageProductBookingActions } from '@store/products/booking/product/package/package-booking.actions';
import {
  ProductBookingActions,
  ProductBookingErrorActions
} from '@store/products/booking/product/product-booking.actions';
import { VoucherProductBookingActions } from '@store/products/booking/product/voucher/voucher-booking.actions';
import { PackageProductActions, TariffProductActions, VoucherProductActions } from '@store/products/product.actions';
import { isEmpty, isNil } from 'lodash';
import { catchError, debounceTime, EMPTY, filter, map, of, switchMap } from 'rxjs';

export const setProductBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    productBookingService = inject(ProductBookingService)
  ) =>
    actions$.pipe(
      ofType(
        TariffProductActions.setTariffBooking,
        VoucherProductActions.setVoucherTariffBooking,
        PackageProductActions.setPackageTariffBooking
      ),
      debounceTime(bookingDebounceTime),
      switchMap(({ bookingProductTypes }) => {
        if (isEmpty(bookingProductTypes)) {
          return EMPTY;
        }

        return productBookingService.setProductBookings$(bookingProductTypes).pipe(
          switchMap((postProductBookings) => {
            return productBookingService.postProductBookings$(postProductBookings).pipe(
              map((postProductBookingsResponse) => {
                const products: BookingProductType[] =
                  productBookingService.mapPostProductBookingsResponseToBookingProductTypes(
                    bookingProductTypes,
                    postProductBookingsResponse
                  );

                return ProductBookingActions.setProductInBookingList(products);
              })
            );
          }),
          catchError(() => of(ProductBookingActions.setProductInBookingList([])))
        );
      })
    ),
  { functional: true }
);

export const setPreferredTariffBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    productBookingService = inject(ProductBookingService),
    preferredBookingService = inject(PreferredBookingService)
  ) =>
    actions$.pipe(
      ofType(TariffProductActions.setPreferredTariffBooking),
      debounceTime(bookingDebounceTime),
      switchMap(({ ticketTypeId, ticketPersonTypeId, count }) => {
        if (isNil(ticketTypeId) || isNil(ticketPersonTypeId) || isNil(count)) {
          return EMPTY;
        }

        return preferredBookingService.setPreferredTariffBooking$(ticketTypeId, ticketPersonTypeId, count).pipe(
          switchMap(({ preferredBookingAndRemovedBookingProductTypes, postProductBookings }) => {
            return productBookingService.postProductBookings$(postProductBookings).pipe(
              map((postProductBookingsResponse) => {
                const products: BookingProductType[] =
                  productBookingService.mapPostProductBookingsResponseToBookingProductTypes(
                    preferredBookingAndRemovedBookingProductTypes,
                    postProductBookingsResponse
                  );

                return ProductBookingActions.removeBookedProductsAndSetPreferredProductInBookingList(products);
              })
            );
          }),
          catchError(() => of(ProductBookingActions.removeBookedProductsAndSetPreferredProductInBookingList([])))
        );
      })
    ),
  { functional: true }
);

export const setProductBookingError$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        ProductBookingActions.setProductInBookingList,
        ProductBookingActions.removeBookedProductsAndSetPreferredProductInBookingList,
        VoucherProductBookingActions.setVoucherProductInBookingList,
        VoucherProductBookingActions.setVoucherForAnonymousProductInBookingList,
        PackageProductBookingActions.setPackageProductInBookingList
      ),
      filter(({ bookingProductTypes }) => isEmpty(bookingProductTypes)),
      switchMap(() => of(ProductBookingErrorActions.setProductInBookingListError()))
    ),
  { functional: true }
);

export const removeProductBookingError$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        ProductBookingActions.removeProductFromBookingList,
        VoucherProductBookingActions.removeVoucherProductFromBookingList,
        VoucherProductBookingActions.removeReleasedVoucherProductFromBookingList,
        PackageProductBookingActions.removePackageFromBookingList
      ),
      filter(({ bookingProductTypes }) => isEmpty(bookingProductTypes)),
      switchMap(() => of(ProductBookingErrorActions.removeProductFromBookingListError()))
    ),
  { functional: true }
);
